import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PERMISSIONS_CONFIG, PERMISSION_MAP } from './roles-dialog.constants';
import { PermissionKey, RolesDialogData } from './roles-dialog.types';
import {
  atLeastOnePermissionSelectedValidator,
  roleDescriptionFormatValidator,
  roleNameDuplicateValidator,
  roleNameFormatValidator,
} from './roles-dialog.validators';
import { filter } from 'rxjs';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { IconName } from '@app/utils/enums/icon-enum';
import { ConfirmModalComponent } from '@app/components/shared/confirm-modal/confirm-modal.component';
import { ROLES_OPTIONS } from '@app/utils/constants/roles-settings.constants';
import { RolesService } from '../services/roles.service';
import { StatusModalComponent } from '@app/components/shared/status-modal/status-modal.component';
import { RoleWithPermissions } from '../interfaces/role-response.interface';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

interface RoleFormValue {
  name: string;
  description: string;
  admin: boolean;
  permissions: Record<PermissionKey, boolean>;
}

@Component({
  selector: 'app-roles-dialog',
  standalone: true,
  imports: [
    NgClass,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSlideToggleModule,
    TranslateModule,
    IconComponent,
    MatProgressSpinnerModule,
  ],
  templateUrl: './roles-dialog.component.html',
  styleUrl: './roles-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RolesDialogComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogRef = inject(MatDialogRef<RolesDialogComponent>);
  private readonly data = inject<RolesDialogData>(MAT_DIALOG_DATA);
  private readonly permissionMap = PERMISSION_MAP;
  private readonly dialog = inject(MatDialog);
  private readonly rolesService = inject(RolesService);

  private initialFormValue: RoleFormValue | null = null;

  public readonly rolesWithPermissions = signal<RoleWithPermissions[]>([]);
  public readonly IconName = IconName;
  public readonly permissionsConfig = PERMISSIONS_CONFIG;
  public roleForm!: FormGroup;

  public readonly titleKey = computed(() =>
    this.data.isEdit
      ? 'shop-settings.roles.actions.edit'
      : 'shop-settings.roles.rolesModal.titleAdd',
  );

  private readonly existingRoleNames = computed(() =>
    this.rolesWithPermissions().map((role) => role.roleName),
  );

  private readonly currentRole = computed(() =>
    this.rolesWithPermissions().find((role) => role.roleId === this.data.role?.id),
  );

  public readonly translationKey = computed(() =>
    this.data.isEdit ? 'shop-settings.roles.rolesModal.save' : 'shop-settings.roles.rolesModal.add',
  );

  public get nameControl(): AbstractControl | null {
    return this.roleForm?.get('name') ?? null;
  }

  public get descriptionControl(): AbstractControl | null {
    return this.roleForm?.get('description') ?? null;
  }

  public get permissionsGroup(): FormGroup {
    return this.roleForm.get('permissions') as FormGroup;
  }

  public ngOnInit(): void {
    this.initForm();
    this.captureInitialFormValue();
    this.setupAdminPermissionsSync();
    this.setupPermissionsErrorTrigger();
    this.loadRolesWithPermissions();
  }

  public onSave(): void {
    this.roleForm.markAllAsTouched();
    this.roleForm.updateValueAndValidity({ onlySelf: false, emitEvent: false });

    const isNameInvalid = !!this.nameControl?.invalid;
    const isDescriptionInvalid = !!this.descriptionControl?.invalid;

    if (isNameInvalid || isDescriptionInvalid) {
      return;
    }

    this.permissionsGroup.markAllAsTouched();
    this.permissionsGroup.markAsDirty();
    this.permissionsGroup.updateValueAndValidity({ onlySelf: true, emitEvent: false });

    if (this.permissionsGroup.invalid) {
      return;
    }

    const { name, description, permissions } = this.roleForm.value as {
      name: string;
      description: string;
      permissions: Record<PermissionKey, boolean>;
    };

    const permissionIds = (Object.keys(permissions) as PermissionKey[])
      .filter((key) => permissions[key])
      .map((key) => this.permissionMap[key]);

    this.dialogRef.close({ name, description, permissionIds });
  }

  public onCancel(): void {
    if (!this.hasFormChanges()) {
      this.dialogRef.close(null);
      return;
    }

    const confirmRef = this.dialog.open(ConfirmModalComponent, {
      ...ROLES_OPTIONS,
    });

    confirmRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef), filter(Boolean))
      .subscribe(() => this.dialogRef.close(null));
  }

  private initForm(): void {
    this.roleForm = this.fb.group({
      name: ['', [Validators.required, roleNameFormatValidator]],
      description: ['', [roleDescriptionFormatValidator]],
      admin: [false],
      permissions: this.fb.group(
        {
          inventory: [false],
          customers: [false],
          orders: [false],
          admins: [false],
          system: [false],
        },
        { validators: atLeastOnePermissionSelectedValidator },
      ),
    });
  }

  private setupAdminPermissionsSync(): void {
    const adminControl = this.roleForm.get('admin');
    const permissions = this.permissionsGroup;

    if (!adminControl) {
      return;
    }

    adminControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isAdmin: boolean) => {
        Object.values(permissions.controls).forEach((control) => {
          control.setValue(isAdmin, { emitEvent: false });
        });

        permissions.updateValueAndValidity({ onlySelf: true });
      });

    permissions.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      const allSelected = Object.values(value as Record<string, boolean>).every(Boolean);

      if (adminControl.value !== allSelected) {
        adminControl.setValue(allSelected, { emitEvent: false });
      }
    });
  }

  private setupPermissionsErrorTrigger(): void {
    const nameControl = this.nameControl;

    if (!nameControl) {
      return;
    }

    nameControl.statusChanges
      .pipe(
        filter((status) => status === 'VALID'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (
          this.permissionsGroup.invalid &&
          !(this.permissionsGroup.touched || this.permissionsGroup.dirty)
        ) {
          this.permissionsGroup.markAsTouched();
          this.permissionsGroup.markAsDirty();
          this.permissionsGroup.updateValueAndValidity({ onlySelf: true });
        }
      });
  }

  private updateNameValidator(): void {
    const currentRoleName = this.data.role?.name ?? '';

    this.nameControl?.setValidators([
      Validators.required,
      roleNameFormatValidator,
      roleNameDuplicateValidator(
        this.existingRoleNames(),
        this.data.isEdit ? currentRoleName : null,
      ),
    ]);
    this.nameControl?.updateValueAndValidity({ emitEvent: false });
  }

  private fillForm(): void {
    const currentName = this.currentRole()?.roleName ?? '';

    this.roleForm.patchValue(
      {
        name: currentName,
        description: this.currentRole()?.description ?? '',
        admin: this.currentRole()?.permissions['SuperAdmins management'] ?? false,
        permissions: {
          inventory: this.currentRole()?.permissions['Inventory management'] ?? false,
          customers: this.currentRole()?.permissions['Customers management'] ?? false,
          orders: this.currentRole()?.permissions['Orders management'] ?? false,
          admins: this.currentRole()?.permissions['Admins management'] ?? false,
          system: this.currentRole()?.permissions['System settings'] ?? false,
        },
      },
      { emitEvent: true },
    );

    this.captureInitialFormValue();
  }

  private loadRolesWithPermissions(): void {
    this.rolesService
      .getRolesWithPermissions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.rolesWithPermissions.set(response.data);
          this.updateNameValidator();

          if (this.data.isEdit) {
            this.fillForm();
          }
        },
        error: () => {
          this.openLoadRolesErrorModal();
        },
      });
  }

  private openLoadRolesErrorModal(
    title = 'shop-settings.roles.errors.errorTitle',
    description = 'shop-settings.roles.errors.errorText',
  ): void {
    this.dialog.open(StatusModalComponent, {
      ...ROLES_OPTIONS,
      data: {
        isSuccess: false,
        title,
        description,
      },
    });
  }

  private captureInitialFormValue(): void {
    this.initialFormValue = this.getNormalizedFormValue();
  }

  private hasFormChanges(): boolean {
    if (!this.initialFormValue) {
      return false;
    }

    const currentValue = this.getNormalizedFormValue();

    return JSON.stringify(currentValue) !== JSON.stringify(this.initialFormValue);
  }

  private getNormalizedFormValue(): RoleFormValue {
    const rawValue = this.roleForm.getRawValue() as RoleFormValue;

    return {
      name: rawValue.name?.trim() ?? '',
      description: rawValue.description?.trim() ?? '',
      admin: !!rawValue.admin,
      permissions: {
        inventory: !!rawValue.permissions.inventory,
        customers: !!rawValue.permissions.customers,
        orders: !!rawValue.permissions.orders,
        admins: !!rawValue.permissions.admins,
        system: !!rawValue.permissions.system,
      },
    };
  }
}
